import React, { useState, useEffect } from 'react';
import { WebMapPicker } from './WebMapPicker';
import { GeografiaService, Estado, Ciudad } from '@/core/application/directory/GeografiaService';
import { CustomSelect } from '@/presentation/components/UI/CustomSelect';
import { MapPin } from 'lucide-react';

export interface LocationData {
  estado_id: number | null;
  ciudad_id: number | null;
  latitud: number | null;
  longitud: number | null;
  direccion_fisica: string;
}

interface UbicacionEditorProps {
  initialData: LocationData;
  onChange: (data: LocationData) => void;
  theme: any; 
}

const DEFAULT_REGION = {
  latitude: 10.4806,
  longitude: -66.9036, // Caracas
};

export const UbicacionEditor = ({ initialData, onChange, theme }: UbicacionEditorProps) => {
  const [mapRegion, setMapRegion] = useState<any>(
    initialData.latitud && initialData.longitud 
      ? { latitude: initialData.latitud, longitude: initialData.longitud }
      : DEFAULT_REGION
  );
  
  const [estados, setEstados] = useState<Estado[]>([]);
  const [ciudades, setCiudades] = useState<Ciudad[]>([]);

  useEffect(() => {
    const loadEstados = async () => {
      try {
        const data = await GeografiaService.getEstados();
        setEstados(data);
      } catch (e) {
        console.error('Error al cargar estados:', e);
      }
    };
    loadEstados();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const estadoId = initialData.estado_id;
      if (estadoId) {
        try {
          const data = await GeografiaService.getCiudadesPorEstado(estadoId);
          setCiudades(data);
        } catch (e) {
          console.error('Error al cargar ciudades:', e);
        }
      } else {
        setCiudades([]);
      }
    };
    loadData();
  }, [initialData.estado_id]);

  const requestGPSLocation = async () => {
    if (!('geolocation' in navigator)) {
      globalThis.alert('Error: Tu navegador no soporta geolocalización.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setMapRegion({ latitude: lat, longitude: lng });
        onChange({ ...initialData, latitud: lat, longitud: lng });
      },
      (error) => {
        console.error(error);
        globalThis.alert('Permiso denegado o error de red: No pudimos acceder a tu GPS. Por favor mueve el mapa manualmente.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <div style={{ width: '100%', marginBottom: '16px' }}>
      {/* Pickers (Obligatorios) */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: theme.text, marginTop: 0 }}>
          1. Selecciona tu ubicación (Obligatorio)
        </h3>
        
        <span id="label-estado" style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px', color: theme.textSecondary }}>Estado</span>
        <CustomSelect
          aria-labelledby="label-estado"
          options={estados.map(est => ({ label: est.nombre_estado, value: est.estado_id }))}
          selectedValue={initialData.estado_id}
          onValueChange={(val) => {
            const numVal = Number(val);
            onChange({ ...initialData, estado_id: numVal, ciudad_id: null });
          }}
          placeholder="Selecciona un Estado..."
          theme={theme}
        />

        <span id="label-ciudad" style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px', marginTop: '12px', color: theme.textSecondary }}>Ciudad</span>
        <CustomSelect
          aria-labelledby="label-ciudad"
          options={ciudades.map(ciu => ({ label: ciu.ciudad_nombre, value: ciu.ciudad_id }))}
          selectedValue={initialData.ciudad_id}
          onValueChange={(val) => {
            const numVal = Number(val);
            onChange({ ...initialData, ciudad_id: numVal });
          }}
          placeholder="Selecciona una Ciudad..."
          theme={theme}
          enabled={ciudades.length > 0}
        />
      </div>

      {/* Mapa (Opcional) */}
      <div style={{ paddingTop: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: theme.text, marginTop: 0 }}>
          2. Mi Consultorio GPS (Recomendado)
        </h3>
        <p style={{ fontSize: '13px', marginBottom: '12px', lineHeight: '18px', color: theme.textSecondary, marginTop: 0 }}>
          Mueve el mapa o presiona el botón para ubicarte con exactitud. Esto ayudará a los pacientes a encontrarte cuando busquen "cerca de mí".
        </p>
        
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '12px' }}>
            <WebMapPicker 
              latitude={mapRegion.latitude}
              longitude={mapRegion.longitude}
              onLocationChange={(lat: number, lng: number) => {
                setMapRegion({ latitude: lat, longitude: lng });
                onChange({ ...initialData, latitud: lat, longitud: lng });
              }}
              theme={theme}
            />
          </div>

          <button 
            onClick={requestGPSLocation}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.card || '#FFFFFF',
              border: `1px solid ${theme.background || '#E2E8F0'}`,
              color: theme.primary,
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              marginBottom: '8px',
              gap: '6px'
            }}
          >
            <MapPin size={18} color={theme.primary} />
            Centrar en mi ubicación actual
          </button>
        </div>
      </div>

      <label htmlFor="direccion-detallada" style={{ display: 'block', fontSize: '16px', fontWeight: 600, marginTop: '16px', marginBottom: '8px', color: theme.text }}>
        Dirección Detallada (Piso, Consultorio)
      </label>
      <textarea
        id="direccion-detallada"
        style={{
          width: '100%',
          border: `1px solid ${theme.background || '#E2E8F0'}`,
          borderRadius: '8px',
          padding: '16px',
          fontSize: '16px',
          color: theme.text,
          backgroundColor: 'transparent',
          minHeight: '80px',
          fontFamily: 'inherit',
          resize: 'vertical',
          boxSizing: 'border-box'
        }}
        placeholder="Ej. Centro Médico Aura, Torre A, Piso 3, Consultorio 302..."
        value={initialData.direccion_fisica || ''}
        onChange={(e) => onChange({ ...initialData, direccion_fisica: e.target.value })}
        rows={3}
      />
    </div>
  );
};
